import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronDown,
  ExternalLink,
  Loader2,
  MapPin,
  Package,
  Search,
  SlidersHorizontal,
  Store,
  X,
} from "lucide-react";
import {
  getPennyItemInternetNumber,
  getPennyItems,
  isPennyItemsMaintenanceError,
  isPennyZipRequiredError,
  PENNY_ITEMS_MAINTENANCE_MESSAGE,
  PENNY_ITEMS_MAINTENANCE_TITLE,
  type PennyItem,
} from "../services/PennyItemsService";
import {
  searchHomeDepotProduct,
  type HomeDepotStoreProductResult,
} from "../services/ProductSearchService";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { toast } from "react-toastify";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";
import EmptyState from "../components/EmptyState";
import { getProductImageUrl } from "../utils/imageUtils";
import { hasHomeDepotPennyAccess } from "../utils/userUtils";
import NearbyResults from "../components/NearbyResults";

interface StoreOption {
  id: string;
  label: string;
  count: number;
}

type PennyListRow =
  | { type: "disclaimer" }
  | { type: "empty" }
  | { type: "item"; item: PennyItem };

export default function PennyItemsProPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { user, updateUser } = useUserContext();
  const parentRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"detected" | "updated" | "price" | "stock">("detected");
  const [selectedStore, setSelectedStore] = useState<string>("all");
  const [showStorePicker, setShowStorePicker] = useState(false);
  const [showMobileSort, setShowMobileSort] = useState(false);
  const [showZipModal, setShowZipModal] = useState(false);
  const [zipInput, setZipInput] = useState("");
  const [savingZip, setSavingZip] = useState(false);
  const [nearbyItemId, setNearbyItemId] = useState<string | null>(null);
  const [nearbyResults, setNearbyResults] = useState<HomeDepotStoreProductResult[]>([]);
  const [nearbyError, setNearbyError] = useState<string | null>(null);
  const [showNearbyResults, setShowNearbyResults] = useState(false);

  const hasAccess = hasHomeDepotPennyAccess(user);
  const missingZipCode = !user?.pennyZipCode;

  const { data: pennyItems = [], isLoading, error, refetch } = useQuery({
    queryKey: ["penny-items-pro"],
    queryFn: getPennyItems,
    enabled: hasAccess && !missingZipCode,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => {
      const status = (err as Error & { status?: number })?.status;
      if (status === 428 || isPennyItemsMaintenanceError(err)) return false;
      return failureCount < 2;
    },
  });

  // The cached user can carry a ZIP the server doesn't have; trust the server's 428 and ask again.
  const needsZipCode = missingZipCode || isPennyZipRequiredError(error);

  // Show zip modal if user needs to set zip code
  useEffect(() => {
    if (hasAccess && needsZipCode) setShowZipModal(true);
  }, [hasAccess, needsZipCode]);

  const handleSaveZipCode = async () => {
    if (!/^\d{5}$/.test(zipInput)) {
      toast.error("Please enter a valid 5-digit zip code");
      return;
    }
    setSavingZip(true);
    try {
      await updateUser({ pennyZipCode: zipInput });
      setShowZipModal(false);
      toast.success("Zip code saved!");
      // Invalidate and refetch user and penny items
      await queryClient.invalidateQueries({ queryKey: ["user"] });
      await queryClient.invalidateQueries({ queryKey: ["penny-items-pro"] });
    } catch {
      toast.error("Failed to save zip code");
    } finally {
      setSavingZip(false);
    }
  };

  // Compute store options from products (only in-stock items)
  const storeOptions = useMemo((): StoreOption[] => {
    const storeMap = new Map<number, { city: string; state: string; count: number }>();
    
    pennyItems.forEach((item) => {
      if (item.current_stock <= 0) return;
      const existing = storeMap.get(item.store_id);
      if (existing) {
        existing.count++;
      } else {
        storeMap.set(item.store_id, { city: item.store_city, state: item.store_state, count: 1 });
      }
    });

    const options: StoreOption[] = [
      { id: "all", label: "All Stores", count: pennyItems.filter(i => i.current_stock > 0).length },
    ];

    Array.from(storeMap.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .forEach(([storeId, data]) => {
        options.push({
          id: String(storeId),
          label: `#${storeId} · ${data.city}`,
          count: data.count,
        });
      });

    return options;
  }, [pennyItems]);

  const filteredItems = useMemo(() => {
    let items = [...pennyItems].filter((item) => item.current_stock > 0);

    if (selectedStore !== "all") {
      items = items.filter((item) => String(item.store_id) === selectedStore);
    }

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      items = items.filter(
        (item) =>
          item.item_name.toLowerCase().includes(search) ||
          item.brand.toLowerCase().includes(search) ||
          item.item_id.includes(search) ||
          item.upc.includes(search) ||
          item.store_sku.toLowerCase().includes(search)
      );
    }

    items.sort((a, b) => {
      switch (sortBy) {
        case "detected":
          return new Date(b.detection_date).getTime() - new Date(a.detection_date).getTime();
        case "updated":
          return new Date(b.item_last_updated_date).getTime() - new Date(a.item_last_updated_date).getTime();
        case "price":
          return a.current_price - b.current_price;
        case "stock":
          return b.current_stock - a.current_stock;
        default:
          return 0;
      }
    });

    return items;
  }, [pennyItems, searchTerm, sortBy, selectedStore]);

  const selectedStoreOption = useMemo(
    () => storeOptions.find((store) => store.id === selectedStore) ?? storeOptions[0],
    [selectedStore, storeOptions]
  );

  const listRows = useMemo((): PennyListRow[] => {
    const disclaimer: PennyListRow = { type: "disclaimer" };
    if (filteredItems.length === 0) return [disclaimer, { type: "empty" }];
    return [disclaimer, ...filteredItems.map((item): PennyListRow => ({ type: "item", item }))];
  }, [filteredItems]);

  const virtualizer = useVirtualizer({
    count: listRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const row = listRows[index];
      if (!row) return 204;
      if (row.type === "disclaimer") return 56;
      if (row.type === "empty") return 260;
      return 204;
    },
    overscan: 5,
  });

  const handleCheckNearby = async (item: PennyItem) => {
    const zipCode = user?.pennyZipCode?.trim() || user?.zipCode?.trim();
    if (!zipCode) {
      setShowZipModal(true);
      return;
    }

    const internetNumber = getPennyItemInternetNumber(item);
    setNearbyItemId(internetNumber);
    setNearbyResults([]);
    setNearbyError(null);
    setShowNearbyResults(true);

    try {
      const response = await searchHomeDepotProduct(internetNumber, zipCode);
      setNearbyResults(response.results);
      if (response.results.length === 0) {
        setNearbyError(t("products.noResultsForProduct"));
      }
    } catch (searchError) {
      setNearbyError(
        searchError instanceof Error
          ? searchError.message
          : t("products.failedToSearchProduct")
      );
    } finally {
      setNearbyItemId(null);
    }
  };

  const closeNearbyResults = () => {
    setShowNearbyResults(false);
    setNearbyItemId(null);
    setNearbyResults([]);
    setNearbyError(null);
  };

  const closeStorePicker = useCallback(() => setShowStorePicker(false), []);
  const selectStore = useCallback((storeId: string) => {
    setSelectedStore(storeId);
    setShowStorePicker(false);
  }, []);

  if (!hasAccess) {
    return (
      <EmptyState
        title="Access Restricted"
        message="Penny items are available on Home Depot Plus and Pro plans."
        actionButton={{ text: "Manage Billing", to: "/settings?tab=billing" }}
      />
    );
  }

  if (showZipModal || needsZipCode) {
    return (
      <div className="flex flex-col h-full bg-white dark:bg-dark-950 items-center justify-center p-4">
        <div className="bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700 p-5 w-full max-w-xs">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-primary-50 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
              <MapPin className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Set Penny Items Pro Location</h2>
              <p className="text-[11px] text-gray-400 dark:text-dark-500">This cannot be changed later</p>
            </div>
          </div>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={5}
            value={zipInput}
            onChange={(e) => setZipInput(e.target.value.replace(/\D/g, ""))}
            placeholder="ZIP Code"
            className="w-full px-3 py-2 border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-gray-900 dark:text-white rounded-lg text-center text-sm tracking-wider focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 placeholder:text-gray-400 dark:placeholder:text-dark-500"
            autoFocus
          />
          <button
            onClick={handleSaveZipCode}
            disabled={savingZip || zipInput.length !== 5}
            className="w-full mt-3 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-200 dark:disabled:bg-dark-700 disabled:text-gray-400 dark:disabled:text-dark-500 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            {savingZip ? "Saving..." : "Continue"}
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) return <LoadingState message="Loading penny items..." />;

  if (error) {
    if (isPennyItemsMaintenanceError(error)) {
      return (
        <ErrorState
          title={PENNY_ITEMS_MAINTENANCE_TITLE}
          message={PENNY_ITEMS_MAINTENANCE_MESSAGE}
        />
      );
    }
    return (
      <ErrorState
        title="Failed to load"
        message={error instanceof Error ? error.message : "Unknown error"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-dark-950">
      {/* Header */}
      <div className="bg-white dark:bg-dark-800 sticky top-0 z-10 border-b border-gray-200/70 dark:border-dark-700">
        <div className="px-3 py-3 sm:px-5">
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 flex-shrink-0 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-dark-700 rounded-xl transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-dark-300" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-lg font-bold tracking-tight text-gray-950 dark:text-white sm:text-xl">
                Penny Items Pro
              </h1>
              <p className="hidden truncate text-[11px] text-gray-500 dark:text-dark-400 sm:block sm:text-xs">
                {filteredItems.length} {filteredItems.length === 1 ? "likely penny find" : "likely penny finds"} near {user?.pennyZipCode}
              </p>
            </div>
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-xl text-primary-600 transition-colors hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-950/40 sm:hidden"
              aria-label="Filter penny items"
              aria-expanded={showMobileSort}
              onClick={() => setShowMobileSort((isOpen) => !isOpen)}
            >
              <SlidersHorizontal className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-3 grid grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-2 sm:flex sm:flex-row">
            <div className="relative order-2 min-w-0 sm:order-1 sm:flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-dark-500" />
              <input
                id="penny-items-search"
                type="text"
                placeholder="Search items…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block h-11 w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-9 text-sm text-gray-900 transition-all placeholder:text-gray-400 focus:border-primary-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/15 dark:border-dark-600 dark:bg-dark-700 dark:text-white dark:placeholder:text-dark-500 dark:focus:border-primary-700 dark:focus:bg-dark-700"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:text-dark-500 dark:hover:text-dark-300"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              )}
            </div>
            <div className="order-1 min-w-0 sm:order-2 sm:flex sm:w-auto sm:gap-2">
              <button
                type="button"
                onClick={() => setShowStorePicker(true)}
                aria-haspopup="dialog"
                aria-expanded={showStorePicker}
                aria-label={`Choose store, ${selectedStoreOption?.label ?? "All Stores"}, ${selectedStoreOption?.count ?? 0} ${(selectedStoreOption?.count ?? 0) === 1 ? "item" : "items"}`}
                className="flex h-11 w-full min-w-0 items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 text-left transition-colors hover:bg-gray-100 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500/15 dark:border-dark-600 dark:bg-dark-700 dark:hover:bg-dark-600 sm:w-56"
              >
                <MapPin className="h-4 w-4 flex-shrink-0 text-primary-500" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-xs font-semibold text-gray-700 dark:text-dark-200">
                  {selectedStoreOption?.label ?? "All Stores"}
                </span>
                <span className="rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-primary-700 dark:bg-primary-900/50 dark:text-primary-300">
                  {selectedStoreOption?.count ?? 0}
                </span>
                <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden />
              </button>
              <div className="relative hidden sm:block sm:w-32">
                <select
                  id="penny-items-sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                  aria-label="Sort penny items"
                  className="h-11 w-full appearance-none rounded-xl border border-gray-200 bg-gray-50 pl-3 pr-8 text-xs font-semibold text-gray-700 outline-none transition-colors hover:bg-gray-100 focus:border-primary-300 focus:ring-2 focus:ring-primary-500/15 dark:border-dark-600 dark:bg-dark-700 dark:text-dark-200 dark:hover:bg-dark-600"
                >
                  <option value="detected">Detected</option>
                  <option value="updated">Updated</option>
                  <option value="price">Price</option>
                  <option value="stock">Stock</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </div>
            </div>
          </div>
          {showMobileSort && (
            <div className="relative mt-2 sm:hidden">
              <select
                id="penny-items-mobile-sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                aria-label="Sort penny items"
                className="h-11 w-full appearance-none rounded-xl border border-primary-200 bg-primary-50/60 pl-3 pr-8 text-xs font-semibold text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/15 dark:border-primary-800 dark:bg-primary-950/30 dark:text-dark-200"
              >
                <option value="detected">Detected</option>
                <option value="updated">Updated</option>
                <option value="price">Price</option>
                <option value="stock">Stock</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary-500" />
            </div>
          )}
        </div>
      </div>

      {/* Items list — disclaimer always first row */}
      <div ref={parentRef} className="flex-1 overflow-auto">
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative",
          }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const row = listRows[virtualItem.index];
            const key =
              row.type === "disclaimer"
                ? "disclaimer"
                : row.type === "empty"
                  ? "empty"
                  : `${row.item.item_id}-${row.item.store_id}`;
            return (
              <div
                key={key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {row.type === "disclaimer" && <PennyDisclaimerRow text={t("products.pennyItemsProListDisclaimer")} />}
                {row.type === "empty" && (
                  <div className="flex flex-col items-center justify-center p-8 min-h-[240px]">
                    <EmptyState
                      title="No items found"
                      message={searchTerm ? "Try a different search" : "Check back later"}
                    />
                  </div>
                )}
                {row.type === "item" && (
                  <PennyItemCard
                    item={row.item}
                    isCheckingNearby={nearbyItemId === getPennyItemInternetNumber(row.item)}
                    onCheckNearby={handleCheckNearby}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      {showNearbyResults && (
        <NearbyResults
          isSearching={nearbyItemId !== null}
          searchError={nearbyError}
          onClose={closeNearbyResults}
          homeDepotSearchResults={nearbyResults}
          isHomeDepot
          retailer="home_depot"
          title={t("products.storeAvailability")}
          searchZipCode={user?.pennyZipCode || user?.zipCode}
        />
      )}
      <StorePickerSheet
        isOpen={showStorePicker}
        options={storeOptions}
        selectedId={selectedStore}
        zipCode={user?.pennyZipCode}
        onClose={closeStorePicker}
        onSelect={selectStore}
      />
    </div>
  );
}

interface StorePickerSheetProps {
  isOpen: boolean;
  options: StoreOption[];
  selectedId: string;
  zipCode?: string;
  onSelect: (storeId: string) => void;
  onClose: () => void;
}

function StorePickerSheet({ isOpen, options, selectedId, zipCode, onSelect, onClose }: StorePickerSheetProps) {
  const [query, setQuery] = useState("");
  const dialogRef = useRef<HTMLDivElement>(null);
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return options;
    return options.filter((store) =>
      store.label.toLowerCase().includes(normalizedQuery) || store.id.includes(normalizedQuery)
    );
  }, [options, query]);

  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    requestAnimationFrame(() => dialogRef.current?.focus());
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center bg-gray-950/55 backdrop-blur-[2px] sheet-backdrop-in"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="penny-store-picker-title"
        tabIndex={-1}
        className="flex max-h-[82vh] w-full max-w-xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl outline-none dark:bg-dark-800 sheet-panel-in"
      >
        <div className="mx-auto mt-2.5 h-1 w-11 rounded-full bg-gray-300 dark:bg-dark-600" aria-hidden />
        <div className="flex items-start gap-3 px-4 pb-3 pt-3 sm:px-5">
          <div className="min-w-0 flex-1">
            <h2 id="penny-store-picker-title" className="text-lg font-bold text-gray-950 dark:text-white">
              Choose a store
            </h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-dark-400">
              {options[0]?.count ?? 0} likely penny finds{zipCode ? ` near ${zipCode}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close store picker"
            className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 transition-colors hover:bg-gray-100 dark:text-dark-300 dark:hover:bg-dark-700"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="px-4 pb-3 sm:px-5">
          <label className="relative block">
            <span className="sr-only">Search stores</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by store number or city"
              className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-500/15 dark:border-dark-600 dark:bg-dark-700 dark:text-white dark:placeholder:text-dark-500"
            />
          </label>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto border-t border-gray-100 px-2 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2 dark:border-dark-700 sm:px-3">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((store) => {
              const isSelected = store.id === selectedId;
              return (
                <button
                  key={store.id}
                  type="button"
                  onClick={() => onSelect(store.id)}
                  aria-label={`${store.label}, ${store.count} ${store.count === 1 ? "item" : "items"}`}
                  aria-pressed={isSelected}
                  className={`mb-1 flex min-h-14 w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors ${
                    isSelected
                      ? "bg-primary-50 text-primary-800 dark:bg-primary-950/45 dark:text-primary-200"
                      : "text-gray-800 hover:bg-gray-50 dark:text-dark-100 dark:hover:bg-dark-800"
                  }`}
                >
                  <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${isSelected ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-500 dark:bg-dark-700 dark:text-dark-300"}`}>
                    {store.id === "all" ? <MapPin className="h-4 w-4" aria-hidden /> : <Store className="h-4 w-4" aria-hidden />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold">{store.label}</span>
                    <span className="mt-0.5 block text-xs font-medium text-gray-500 dark:text-dark-400">
                      {store.count} {store.count === 1 ? "item" : "items"}
                    </span>
                  </span>
                  {isSelected && (
                    <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 text-white">
                      <Check className="h-4 w-4" aria-hidden />
                    </span>
                  )}
                </button>
              );
            })
          ) : (
            <p className="px-4 py-10 text-center text-sm text-gray-500 dark:text-dark-400">No stores found</p>
          )}
        </div>
      </section>
    </div>
  );
}

function PennyDisclaimerRow({ text }: { text: string }) {
  return (
    <div className="flex gap-2 px-3 py-2.5 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200/80 dark:border-amber-800/60">
      <AlertTriangle className="mt-0.5 h-[18px] w-[18px] flex-shrink-0 text-amber-700 dark:text-amber-500" aria-hidden />
      <p className="text-[12px] leading-snug text-amber-900 dark:text-amber-100/90">{text}</p>
    </div>
  );
}

interface PennyItemCardProps {
  item: PennyItem;
  isCheckingNearby: boolean;
  onCheckNearby: (item: PennyItem) => void;
}

function getReadyStatus(detectionDate: string): { isReady: boolean; timeRemaining?: string } {
  const elapsed = Date.now() - new Date(detectionDate).getTime();
  const remaining = 24 * 60 * 60 * 1000 - elapsed;

  if (remaining <= 0) return { isReady: true };

  const totalMinutes = Math.ceil(remaining / (60 * 1000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const timeRemaining = hours > 0
    ? minutes > 0
      ? `${hours}h ${minutes}m`
      : `${hours}h`
    : `${minutes}m`;

  return { isReady: false, timeRemaining };
}

function getHomeDepotProductUrl(item: PennyItem): string {
  const fallbackUrl = `https://www.homedepot.com/p/${getPennyItemInternetNumber(item)}`;
  const productUrl = item.product_link || fallbackUrl;

  try {
    const url = new URL(productUrl);
    url.searchParams.set("store", String(item.store_id));
    return url.toString();
  } catch {
    const separator = productUrl.includes("?") ? "&" : "?";
    return `${productUrl}${separator}store=${item.store_id}`;
  }
}

function PennyItemCard({ item, isCheckingNearby, onCheckNearby }: PennyItemCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const readyStatus = getReadyStatus(item.detection_date);

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(price);
  
  // Clean location - hide if empty or "NO_LOCATION"
  const hasLocation = item.location && item.location !== "NO_LOCATION" && item.location.trim() !== "";

  return (
    <article className="border-b border-gray-200/80 bg-white px-3 py-4 dark:border-dark-700 dark:bg-dark-800 sm:px-5">
        <div className="flex gap-3 sm:gap-4">
          {/* Image */}
          <div className="relative h-28 w-28 flex-shrink-0 overflow-hidden rounded-2xl bg-gray-100 dark:bg-dark-700 sm:h-32 sm:w-32">
            {!imageLoaded && <div className="absolute inset-0 bg-gray-200 dark:bg-dark-600 animate-pulse" />}
            {item.image_link ? (
              <img
                src={getProductImageUrl(item.image_link)}
                alt={item.item_name}
                className={`h-full w-full object-contain p-1.5 transition-opacity ${imageLoaded ? "opacity-100" : "opacity-0"}`}
                onLoad={() => setImageLoaded(true)}
                onError={(e) => { e.currentTarget.style.display = "none"; setImageLoaded(true); }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-dark-500">
                <Package className="h-8 w-8" strokeWidth={1.5} aria-hidden />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="min-w-0 flex-1 py-0.5">
            {/* Title */}
            <h2 className="line-clamp-2 text-[15px] font-bold leading-snug tracking-[-0.01em] text-gray-950 dark:text-white sm:text-base">
              {item.item_name}
            </h2>

            {/* Price Row */}
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="rounded-full bg-red-500 px-3 py-1 text-sm font-bold tabular-nums text-white">
                {formatPrice(item.current_price)}
              </span>
              {item.previous_price > 0.01 && (
                <span className="text-xs tabular-nums text-gray-400 line-through dark:text-dark-500">
                  {formatPrice(item.previous_price)}
                </span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <p className="text-xs font-semibold text-green-600 dark:text-green-400">
                {item.current_stock} in stock
              </p>
              {!readyStatus.isReady && (
                <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                  Ready in {readyStatus.timeRemaining}
                </span>
              )}
            </div>
            <div className="mt-1.5 space-y-1 text-xs text-gray-500 dark:text-dark-300">
              <p className="flex min-w-0 items-center gap-1.5">
                <Store className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                <span className="truncate">{item.store_city}, {item.store_state}</span>
              </p>
              <p className="flex min-w-0 items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                <span className="truncate">{hasLocation ? item.location : "No location listed"}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 divide-x divide-gray-200 border-y border-gray-200/80 dark:divide-dark-600 dark:border-dark-700">
          <button
            type="button"
            onClick={() => onCheckNearby(item)}
            disabled={isCheckingNearby}
            className="flex min-h-11 items-center justify-center gap-2 px-2 text-sm font-bold text-primary-700 transition-colors hover:bg-primary-50 disabled:cursor-wait disabled:opacity-60 dark:text-primary-300 dark:hover:bg-primary-950/30"
          >
            {isCheckingNearby ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
            <span>{isCheckingNearby ? "Checking…" : "Check stock"}</span>
          </button>
          <a
            href={getHomeDepotProductUrl(item)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-11 items-center justify-center gap-2 px-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50 dark:text-primary-300 dark:hover:bg-primary-950/30"
          >
            <ExternalLink className="h-4 w-4" />
            <span>Home Depot</span>
          </a>
        </div>
    </article>
  );
}
